
import React, { useEffect, useState } from "react";
import "./style.css";
import { Table, TimePicker, Spin, Alert } from "antd";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { getEmployeeRecordsByDate, updateAttendanceTime } from "../../../service/adminAttendanceService";

const EditAttendanceAdminModal = ({ handleCancel, employeeId, date, onChange, isView }) => {
  const [data, setData] = useState([]);
  const [recordTimes, setRecordTimes] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  const fetchRecords = async () => {
    try {
      setFetchLoading(true);
      setFetchError(null);
      console.log("Fetching records for EPF:", employeeId, "Date:", date);

      // Normalize date to YYYY-MM-DD to avoid timezone issues
      const normalizedDate = date ? dayjs(date).format("YYYY-MM-DD") : null;
      console.log("Normalized date:", normalizedDate);

      if (!employeeId || !normalizedDate) {
        setFetchError("Employee ID or Date is missing");
        setFetchLoading(false);
        return;
      }

      const result = await getEmployeeRecordsByDate(employeeId, normalizedDate);
      console.log("API result:", result);

      if (!result || (Array.isArray(result) && result.length === 0)) {
        setFetchError("No attendance records found for this employee on this date");
        setData([]);
        setRecordTimes({});
        setFetchLoading(false);
        return;
      }

      const records = Array.isArray(result) ? result : [];
      const initialTimes = {};
      records.forEach((record) => {
        initialTimes[record._id] = dayjs(record.time, "HH:mm:ss");
      });
      setRecordTimes(initialTimes);
      setData(records);
    } catch (err) {
      console.error("Error fetching records:", err);
      const errorMsg = err?.response?.data?.message || err?.message || "Failed to load attendance records";
      setFetchError(errorMsg);
      setData([]);
    } finally {
      setFetchLoading(false);
    }
  };

  const handleReset = () => {
    fetchRecords();
  };

  const handleChangeTime = (time, rowId) => {
    setRecordTimes((prev) => ({
      ...prev,
      [rowId]: time,
    }));
  };

  useEffect(() => {
    if (employeeId && date) {
      fetchRecords();
    } else {
      console.warn("Modal opened without employeeId or date:", { employeeId, date });
      setFetchError("Employee ID or Date not provided");
    }
  }, [employeeId, date]);

  const handleSubmit = async (recordId) => {
    const recordTime = recordTimes[recordId].format("HH:mm:ss");
    const attendanceId = recordId;
    try {
      const updatedAttendance = await updateAttendanceTime(attendanceId, recordTime, setLoading);
      console.log("Successfully updated:", updatedAttendance);
      onChange();
    } catch (error) {
      console.error("Failed to update attendance");
    }
  };

  const columns = [
    {
      title: "Date",
      dataIndex: "date",
      render: (date) => {
        const formattedDate = new Date(date).toISOString().split("T")[0];
        return formattedDate;
      },
    },
    {
      title: "Time",
      dataIndex: "time",
      render: (_, record) => (
        <div>
          {!isView && (
            <TimePicker
              disabled={isView}
              value={recordTimes[record._id] || null}
              className="input-group-text"
              onChange={(time) => handleChangeTime(time, record._id)}
              format="HH:mm"
              style={{ width: "207px" }}
            />
          )}
          {isView && (recordTimes[record._id] ? recordTimes[record._id].format("HH:mm:ss") : record.time || "--")}
        </div>
      ),
    },
    {
      title: "Actions",
      render: (text, record) => (
        <div>
          {!isView && (
            <button className="btn btn-submit me-2" onClick={() => handleSubmit(record._id)}>
              {loading ? <Spin size="small" /> : "Save"}
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="modal-content">
      <div className="modal-header border-0 custom-modal-header">
        <div className="page-title">
          {!isView && <h4>Edit Attendances</h4>}
          {isView && <h4>Attendances</h4>}
          <p className="mb-0">Manage Your Attendances</p>
        </div>
      </div>
      <div className="modal-body custom-modal-body">
        <div className="row mb-3">
          <div className="col-12">
            <div className="input-blocks">
              <label>
                <strong>Employee: </strong>
                {data.length > 0
                  ? (() => {
                      const emp = data[0].employee;
                      const epf = emp?.epf_number || "N/A";
                      const name =
                        emp?.full_name ||
                        `${emp?.first_name || ""} ${emp?.last_name || ""}`.trim() ||
                        "Unknown";
                      return `${epf} - ${name}`;
                    })()
                  : employeeId
                  ? `EPF: ${employeeId}`
                  : "N/A"}
              </label>
            </div>
          </div>
        </div>

        {fetchError && (
          <Alert
            message={fetchError}
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {fetchLoading ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Spin size="large" />
            <p style={{ marginTop: 12, color: "#888" }}>Loading attendance records...</p>
          </div>
        ) : (
          <Table
            dataSource={data}
            columns={columns}
            pagination={false}
            rowKey="_id"
          />
        )}

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "20px" }}>
          <div></div>
          <div>
            {!isView && (
              <button className="btn btn-reset me-2" onClick={handleReset}>
                Reset
              </button>
            )}
            <button type="button" className="btn btn-cancel me-2" onClick={handleCancel}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

EditAttendanceAdminModal.propTypes = {
  handleCancel: PropTypes.func.isRequired,
  employeeId: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  isView: PropTypes.bool.isRequired,
};

export default EditAttendanceAdminModal;


